/**
 * PATH: packages/ui/src/kanban/components/card-detail/drawer-sections/ActionSpineSection.tsx
 * FILE_ID: UI-KANBAN-DRAWER-ACTION-SPINE-001
 * PURPOSE: The ACTION spine — the drawer's most prominent block, directly
 *   under the header: the card's action date + "what's the next action"
 *   note, inline-editable, with an overdue signal.
 * WHY: ARC-4 U02 — operator: "the client would rather have action dates …
 *   this is card structuring". The action_date/action_note FIELDS already
 *   shipped (FEAT-2026-06-29-KANBAN-ACTION-DATE); this arc restructures the
 *   drawer AROUND them so the next action reads in seconds without scrolling.
 *   The property-list Action row moved here — same editing state, same
 *   patchCard writes, one home (no duplicate write paths).
 * FLOW:
 *   ┌──────────────────┐   ┌──────────────────┐   ┌─────────────────────┐
 *   │ editing.actionDate│ → │ DateField + note │ → │ patchCard(action_*) │
 *   │ /actionNote state │   │ (this component) │   │ PATCH /cards/<id>/  │
 *   └──────────────────┘   └──────────────────┘   └─────────────────────┘
 * RELATED:
 *   - ../useCardDrawerEditing.ts — owns the state + save pipeline.
 *   - ./CardPropertyList.tsx — the Action row's previous home (removed).
 */
import { Target } from 'lucide-react'

import type { Card } from '../../../types'
import { DateField } from '../../DateField'
import { previousDateLine } from '../../KanbanCardDuePills'
import { formatDmMonthShort } from '../../../utils/localDate'
import { GHOST_CONTROL } from '../drawerChrome'
import type { DrawerEditing } from '../drawerHookTypes'

export interface ActionSpineSectionProps {
  card: Card
  editing: DrawerEditing
}

/** Local YYYY-MM-DD "today" — action dates are date-only fields. */
function todayIso(): string {
  const d = new Date()
  const m = `${d.getMonth() + 1}`.padStart(2, '0')
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

export function ActionSpineSection({ card, editing }: ActionSpineSectionProps) {
  const {
    actionDate, setActionDate, actionNote, setActionNote,
    patchCard, dirtyFieldsRef,
  } = editing

  const overdue = !!actionDate && actionDate.slice(0, 10) < todayIso()

  return (
    <section
      data-testid="drawer-action-spine"
      className={[
        'rounded-xl border px-3.5 py-3',
        overdue
          ? 'border-red-300 bg-red-50/60 dark:border-red-900/60 dark:bg-red-950/20'
          : 'border-blue-200/70 bg-blue-50/40 dark:border-blue-900/50 dark:bg-blue-950/20',
      ].join(' ')}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span
          className={[
            'inline-flex shrink-0 items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em]',
            overdue
              ? 'text-red-600 dark:text-red-400'
              : 'text-blue-700 dark:text-blue-300',
          ].join(' ')}
        >
          <Target className="h-3.5 w-3.5" aria-hidden="true" />
          {overdue ? 'Action overdue' : 'Action'}
        </span>
        <DateField
          ariaLabel="Action date"
          placeholder="Action date"
          value={actionDate || null}
          onChange={(iso) => {
            setActionDate(iso ?? '')
            if (iso !== (card.action_date ?? null)) {
              void patchCard('action_date', { action_date: iso } as never)
            }
          }}
          className={`${GHOST_CONTROL} w-[150px] font-medium`}
        />
        {/* P3 (card design audit 2026-07-07) — date-history whisper: the
            server-denormalised previous action date, one shared gate
            (previousDateLine) with the card-face pills so the two surfaces
            can never disagree about "was it moved". */}
        {(() => {
          const prev = previousDateLine(card.action_date, card.previous_action_date)
          if (!prev) return null
          return (
            <span
              className="text-[11px] text-red-500/90 line-through dark:text-red-400/90"
              title={`Previously ${formatDmMonthShort(prev)}`}
              data-testid="drawer-action-spine-previous-date"
            >
              was {formatDmMonthShort(prev)}
            </span>
          )
        })()}
      </div>
      <input
        type="text"
        aria-label="Action note"
        placeholder="What's the next action?"
        value={actionNote}
        onChange={(e) => {
          setActionNote(e.target.value)
          dirtyFieldsRef.current.add('actionNote')
        }}
        onBlur={() => {
          dirtyFieldsRef.current.delete('actionNote')
          const next = actionNote.trim()
          if (next !== (card.action_note ?? '')) {
            void patchCard('action_note', { action_note: next } as never)
          }
        }}
        className={`${GHOST_CONTROL} mt-1.5 w-full text-[14px]`}
      />
    </section>
  )
}
